import { Permission, Role } from '@tp-vision/roles-permissions';
import jwtDecode from 'jwt-decode';
import { isEqual } from 'lodash';
import { useContext, useState } from 'react';
import { AuthenticationContext } from './AuthenticationProvider';

export interface AuthUser {
  subId?: string;
  name?: string;
  givenName?: string;
  familyName?: string;
  email?: string;
  permissions?: Permission[];
  roles?: Role[];
  'https://keat.io/audiences'?: string[];
  locale: string;
  universalLocale: string;
  tempUnit: string;
  // undefined until the token has been decoded at least once; null once decoded and confirmed
  // absent (the user has no organization yet)
  organizationId?: string | null;
}

export function useAuthentication() {
  const context = useContext(AuthenticationContext);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [locale, setLocale] = useState<string>('en-GB');
  const [universalLocale, setUniversalLocale] = useState<string>('');
  const [tempUnit, setTempUnit] = useState<string>('Celsius');
  const [organizationId, setOrganizationId] = useState<string | null | undefined>(undefined);

  if (!context) {
    throw new Error('useAuthentication must be used within an AuthenticationProvider');
  }

  const auth = useContext(context);

  if (auth.isAuthenticated && permissions.length === 0) {
    auth.getAccessTokenSilently().then((token) => {
      const decodedToken = jwtDecode<{
        permissions?: Permission[];
        'https://wave.ppds.com/user_roles'?: Role[];
        'https://wave.ppds.com/locale'?: string;
        'https://wave.ppds.com/universalLocale'?: string;
        'https://wave.ppds.com/tempUnit'?: string;
        'https://wave.ppds.com/organization_id'?: string;
      }>(token);

      const tokenRoles = decodedToken['https://wave.ppds.com/user_roles'] ?? [];
      const tokenPermissions = decodedToken.permissions ?? [];
      const tokenlocale = decodedToken['https://wave.ppds.com/locale'] ?? 'en-GB';
      const tokenUniversalLocale = decodedToken['https://wave.ppds.com/universalLocale'] ?? '';
      const tokenTempUnit = decodedToken['https://wave.ppds.com/tempUnit'] ?? 'Celsius';
      const tokenOrganizationId = decodedToken['https://wave.ppds.com/organization_id'] ?? null;
      if (!isEqual(tokenRoles, roles)) {
        setRoles(tokenRoles);
      }

      if (!isEqual(tokenPermissions, permissions)) {
        setPermissions(tokenPermissions);
      }
      if (!isEqual(tokenlocale, locale)) {
        setLocale(tokenlocale);
      }
      if (!isEqual(tokenUniversalLocale, universalLocale)) {
        setUniversalLocale(tokenUniversalLocale);
      }
      if (!isEqual(tokenTempUnit, tempUnit)) {
        setTempUnit(tokenTempUnit);
      }
      if (!isEqual(tokenOrganizationId, organizationId)) {
        setOrganizationId(tokenOrganizationId);
      }
    });
  }

  const user: AuthUser = {
    ...auth.user,
    subId: auth.user?.sub,
    name: auth.user?.name,
    givenName: auth.user?.given_name,
    familyName: auth.user?.family_name,
    email: auth.user?.email,
    'https://keat.io/audiences': auth.user?.['https://keat.io/audiences'],
    permissions,
    roles,
    locale,
    universalLocale,
    tempUnit,
    organizationId,
  };

  return {
    ...auth,
    user,
  };
}
